// Catalog item readers used by the "Nova proposta" flow to normalize portfolio,
// empreendimentos and external listings into the native imovel payload.
// Extracted verbatim from RentFlowsIndex.vue.

export const valueFrom = (item, ...keys) => {
  if (!item) return null;
  const attrs = item.additional_attributes || item.additionalAttributes || {};
  const key = keys.find(currentKey => {
    const value = item[currentKey] ?? attrs[currentKey];
    return value !== undefined && value !== null && value !== '';
  });

  if (!key) return null;
  return item[key] !== undefined && item[key] !== null && item[key] !== ''
    ? item[key]
    : attrs[key];
};

export const itemCode = item =>
  valueFrom(
    item,
    'codigo',
    'code',
    'property_code',
    'propertyCode',
    'external_id',
    'externalId',
    'house_id',
    'houseId',
    'sku'
  ) || valueFrom(item, 'id');

export const itemTitle = item =>
  valueFrom(item, 'titulo', 'title', 'name', 'address') || 'Imóvel sem título';

export const itemLocation = item =>
  [
    valueFrom(item, 'endereco', 'address', 'street'),
    valueFrom(item, 'bairro', 'neighborhood'),
    valueFrom(item, 'cidade', 'city'),
    valueFrom(item, 'uf', 'state'),
  ]
    .filter(Boolean)
    .join(', ');

export const itemPublicLink = item =>
  valueFrom(
    item,
    'link_externo',
    'linkExterno',
    'external_url',
    'externalUrl',
    'public_url',
    'publicUrl',
    'contactWebsite',
    'url'
  );

export const itemValue = item => {
  const saleValue = valueFrom(
    item,
    'valor_venda',
    'valorVenda',
    'sale_price',
    'salePrice',
    'price'
  );
  const rentValue = valueFrom(
    item,
    'valor_aluguel',
    'valorAluguel',
    'rent_price',
    'rentPrice'
  );
  const attrs =
    valueFrom(item, 'additional_attributes', 'additionalAttributes') || {};
  return saleValue || rentValue || attrs.min_price || attrs.minPrice || '';
};

export const itemSource = mode => {
  switch (mode) {
    case 'empreendimentos':
      return 'imudei.empreendimentos';
    case 'portfolio':
      return 'imudei.carteira';
    default:
      return 'imobiliaria-api.Properties';
  }
};

export const itemFinalidade = item =>
  valueFrom(item, 'finalidade', 'transaction_type', 'transactionType') ||
  (valueFrom(item, 'valor_aluguel', 'valorAluguel', 'rent_price', 'rentPrice')
    ? 'aluguel'
    : 'venda');

export const normalizedExternalItem = (item, mode) => {
  const source = itemSource(mode);
  return {
    id: valueFrom(item, 'id'),
    external_id: valueFrom(item, 'external_id', 'externalId', 'id'),
    codigo: itemCode(item),
    titulo: itemTitle(item),
    descricao: valueFrom(item, 'descricao', 'description'),
    tipo: valueFrom(item, 'tipo', 'type'),
    finalidade: itemFinalidade(item),
    status: valueFrom(item, 'status') || 'disponivel',
    valor_venda: valueFrom(
      item,
      'valor_venda',
      'valorVenda',
      'sale_price',
      'salePrice',
      'price'
    ),
    valor_aluguel: valueFrom(
      item,
      'valor_aluguel',
      'valorAluguel',
      'rent_price',
      'rentPrice'
    ),
    valor_iptu: valueFrom(item, 'valor_iptu', 'valorIptu', 'property_tax'),
    valor_condominio: valueFrom(
      item,
      'valor_condominio',
      'valorCondominio',
      'condo_price'
    ),
    quartos: valueFrom(item, 'quartos', 'bedrooms'),
    banheiros: valueFrom(item, 'banheiros', 'bathrooms'),
    vagas_garagem: valueFrom(item, 'vagas_garagem', 'vagasGaragem'),
    area_util: valueFrom(item, 'area_util', 'areaUtil'),
    area_total: valueFrom(item, 'area_total', 'areaTotal'),
    bairro: valueFrom(item, 'bairro', 'neighborhood'),
    cidade: valueFrom(item, 'cidade', 'city'),
    uf: valueFrom(item, 'uf', 'state'),
    cep: valueFrom(item, 'cep', 'zip_code', 'zipCode'),
    latitude: valueFrom(item, 'latitude', 'lat'),
    longitude: valueFrom(item, 'longitude', 'lng'),
    endereco: valueFrom(item, 'endereco', 'address', 'street'),
    link_externo: itemPublicLink(item),
    portal_origem: valueFrom(item, 'portal_origem', 'portalOrigem') || source,
    source,
    midias: valueFrom(item, 'midias', 'media', 'images', 'photos') || [],
    caracteristicas:
      valueFrom(item, 'caracteristicas', 'features', 'amenities') || [],
    additional_attributes: {
      ...(valueFrom(item, 'additional_attributes', 'additionalAttributes') ||
        {}),
      materialized_from_catalog: mode,
      materialized_for: 'property_offer_flow',
      materialized_as_native: true,
      in_house_listing: true,
      in_house_listing_label: 'Imóveis na casa',
      source,
    },
  };
};

export const materializedExternalIds = payload =>
  [
    payload.external_id ? String(payload.external_id) : '',
    payload.external_id && payload.portal_origem
      ? `${payload.portal_origem}:${payload.external_id}`
      : '',
  ].filter(Boolean);
